// @flow
import * as React from 'react'
import styled from 'styled-components'
import { Container, Row, config, Col } from 'react-awesome-styled-grid'
import { Navigation, Pagination, A11y, EffectFade } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import type {
  ContentBlockSpotlightSectionType,
  SpotlightCardsType
} from '../../../services/contentfulMarketingLandingPages'
import { P, colors, H250 } from '../../shared'
import { OutlineOnFocusVisible } from '../../shared/buttons'
import { LeftSliderArrow } from '../../shared/icons/LeftSliderArrow'
import { RightSliderArrow } from '../../shared/icons/RightSliderArrow'
import { FadeInImage } from '@components/shared/FadeInImage'
import { isChromeForIOs145 } from '@helpers/utils'
import SharedCta from '../SharedCta'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { MARKS } from '@contentful/rich-text-types'

const options = {
  renderNode: {
    [MARKS.CODE]: (node: any, children: any): React.Node => {
      return <div dangerouslySetInnerHTML={{ __html: children }} />
    }
  }
}

export default function SpotlightSection (props: {
  block: ContentBlockSpotlightSectionType,
  isFirst: boolean,
  isSingleContentBlock: boolean
}): React.Node {
  const {
    id,
    sectionTitle,
    isFirstImageUsedForAll,
    isDarkTheme,
    spotlightCards
  } = props.block

  if (!spotlightCards.length) {
    return null
  }

  return (
    <Section isDarkTheme={isDarkTheme} isFirst={props.isFirst}>
      <Container>
        <Row>
          <Col
            xs={4}
            md={6}
            lg={12}
            align='center'
            justify='center'
            offset={{
              md: 1
            }}
          >
            <MobileSlider isDarkTheme={isDarkTheme}>
              <SpotlightHr />
              <SectionHeader isDarkTheme={isDarkTheme}>
                {sectionTitle}
              </SectionHeader>

              {isFirstImageUsedForAll && (
                <MobileFirstImage>
                  <FadeInImage
                    src={spotlightCards[0].squareImage}
                    width={600}
                    height={600}
                  />
                </MobileFirstImage>
              )}

              <Swiper
                modules={[Navigation, Pagination, A11y, EffectFade]}
                spaceBetween={0}
                slidesPerView={1}
                pagination={{
                  clickable: true,
                  type: 'bullets'
                }}
                cssMode={isChromeForIOs145()}
              >
                {spotlightCards.map(
                  (
                    spotlight: SpotlightCardsType,
                    index: number
                  ): React.Node => {
                    const description = documentToReactComponents(spotlight.descriptionText, options)

                    return (
                      <SwiperSlide key={`spotlight-${id}-${index}`}>
                        <MobileSlide extraMargin={!spotlight.cta}>
                          {spotlight &&
                            spotlight.squareImage &&
                            !isFirstImageUsedForAll && (
                            <SpotlightImg>
                              <FadeInImage
                                src={spotlight.squareImage}
                                width={600}
                                height={600}
                              />
                            </SpotlightImg>
                          )}

                          <H250
                            color={
                              isDarkTheme ? colors.sand : colors.midnightBlue
                            }
                            styles={{ marginBottom: '15px' }}
                          >
                            {spotlight.headerText}
                          </H250>

                          <Description isDarkTheme={isDarkTheme}>
                            {description}
                          </Description>

                          {!!spotlight.cta && (
                            <SharedCta cta={spotlight.cta} />
                          )}
                        </MobileSlide>
                      </SwiperSlide>
                    )
                  }
                )}
              </Swiper>
            </MobileSlider>
          </Col>

          <DesktopSlider isDarkTheme={isDarkTheme}>
            {isFirstImageUsedForAll && (
              <>
                <FirstImageContainer>
                  <FirstImage>
                    <FadeInImage
                      src={spotlightCards[0].squareImage}
                      width={600}
                      height={600}
                    />
                  </FirstImage>
                </FirstImageContainer>

                <DesktopFirstImageOnly>
                  <DesktopNavigation>
                    <SpotlightHr isDarkTheme={isDarkTheme}></SpotlightHr>
                    <div>
                      <SectionHeader isDarkTheme={isDarkTheme}>
                        {sectionTitle}
                      </SectionHeader>
                      <ArrowWrapper>
                        <ArrowButton
                          className={'previous slidePrev-btn-spotlight'}
                        >
                          <LeftSliderArrow />
                        </ArrowButton>
                        <ArrowButton className={'slideNext-btn-spotlight'}>
                          <RightSliderArrow />
                        </ArrowButton>
                      </ArrowWrapper>
                    </div>
                  </DesktopNavigation>

                  <Swiper
                    modules={[Navigation, Pagination, A11y, EffectFade]}
                    spaceBetween={0}
                    slidesPerView={1}
                    effect='fade'
                    fadeEffect={{
                      crossFade: true
                    }}
                    pagination={{
                      clickable: true,
                      type: 'bullets'
                    }}
                    navigation={{
                      prevEl: '.slidePrev-btn-spotlight',
                      nextEl: '.slideNext-btn-spotlight'
                    }}
                    cssMode={isChromeForIOs145()}
                  >
                    {spotlightCards.map(
                      (
                        spotlight: SpotlightCardsType,
                        index: number
                      ): React.Node => {
                        const description = documentToReactComponents(spotlight.descriptionText, options)

                        return (
                          <SwiperSlide key={`spotlight-slider-${index}`}>
                            <NoImageDesktopVie>
                              <AlignBottomDiv extraMargin={!spotlight.cta}>
                                <div>
                                  <H250
                                    color={
                                      isDarkTheme
                                        ? colors.sand
                                        : colors.midnightBlue
                                    }
                                    styles={{ marginBottom: '15px' }}
                                  >
                                    {spotlight.headerText}
                                  </H250>
                                  <Description isDarkTheme={isDarkTheme} marginBottom={25}>
                                    {description}
                                  </Description>

                                  {!!spotlight.cta && (
                                    <SharedCta cta={spotlight.cta} />
                                  )}
                                </div>
                              </AlignBottomDiv>
                            </NoImageDesktopVie>
                          </SwiperSlide>
                        )
                      }
                    )}
                  </Swiper>
                </DesktopFirstImageOnly>
              </>
            )}

            {!isFirstImageUsedForAll && (
              <>
                <DesktopNavigation>
                  <SpotlightHr isDarkTheme={isDarkTheme}></SpotlightHr>
                  <div>
                    <SectionHeader isDarkTheme={isDarkTheme}>
                      {sectionTitle}
                    </SectionHeader>
                    <ArrowWrapper>
                      <ArrowButton
                        className={'previous slidePrev-btn-spotlight'}
                      >
                        <LeftSliderArrow />
                      </ArrowButton>
                      <ArrowButton className={'slideNext-btn-spotlight'}>
                        <RightSliderArrow />
                      </ArrowButton>
                    </ArrowWrapper>
                  </div>
                </DesktopNavigation>

                <Swiper
                  modules={[Navigation, Pagination, A11y, EffectFade]}
                  spaceBetween={0}
                  slidesPerView={1}
                  effect='fade'
                  fadeEffect={{
                    crossFade: true
                  }}
                  pagination={{
                    clickable: true,
                    type: 'bullets'
                  }}
                  navigation={{
                    prevEl: '.slidePrev-btn-spotlight',
                    nextEl: '.slideNext-btn-spotlight'
                  }}
                  shortSwipes={false}
                  cssMode={isChromeForIOs145()}
                >
                  {spotlightCards.map(
                    (
                      spotlight: SpotlightCardsType,
                      index: number
                    ): React.Node => {
                      const description = documentToReactComponents(spotlight.descriptionText, options)

                      return (
                        <SwiperSlide key={`spotlight-slider-${index}`}>
                          <DesktopView>
                            <SpotlightImg>
                              {spotlight &&
                                spotlight.squareImage &&
                                !isFirstImageUsedForAll && (
                                <FadeInImage
                                  src={spotlight.squareImage}
                                  width={600}
                                  height={600}
                                />
                              )}
                            </SpotlightImg>

                            <AlignBottomDiv>
                              <div>
                                <H250
                                  color={
                                    isDarkTheme
                                      ? colors.sand
                                      : colors.midnightBlue
                                  }
                                  styles={{ marginBottom: '15px' }}
                                >
                                  {spotlight.headerText}
                                </H250>
                                <Description isDarkTheme={isDarkTheme} marginBottom={25}>
                                  {description}
                                </Description>
                                {!!spotlight.cta && (
                                  <SharedCta cta={spotlight.cta} />
                                )}
                              </div>
                            </AlignBottomDiv>
                          </DesktopView>
                        </SwiperSlide>
                      )
                    }
                  )}
                </Swiper>
              </>
            )}
          </DesktopSlider>
        </Row>
      </Container>
    </Section>
  )
}

const Section = styled.div`
  background-color: ${(props: any): any =>
    props.isDarkTheme ? colors.midnightBlue : colors.sand};
  margin: ${(props: any): string => (props.isFirst ? '-75px 0 0 0' : '0')};
  padding: ${(props: any): string =>
    props.isFirst ? '125px 0 65px' : '50px 0 65px'};
  ${(props: any): string => config(props).media.md`
    padding: ${(props: any): string =>
      props.isFirst ? '155px 0 50px' : '50px 0'};
  `}

  .swiper-pagination-bullet {
    height: 11px;
    width: 11px;
    margin: 0 6px !important;
    background-color: transparent !important;
    border: 1px solid
      ${(props: any): any =>
        props.isDarkTheme ? colors.sand : colors.midnightBlue} !important;
    opacity: 1 !important;
    ${OutlineOnFocusVisible};
  }
  .swiper-pagination-bullet-active {
    background-color: ${(props: any): any =>
      props.isDarkTheme ? colors.sand : colors.midnightBlue} !important;
    border: 1px solid
      ${(props: any): any =>
        props.isDarkTheme ? colors.sand : colors.midnightBlue} !important;
  }
`
const Description = styled.div`
  color:  ${(props: any): any => props.isDarkTheme ? colors.sand : colors.midnightBlue};
  font-family: 'Good Sans Light';
  font-style: normal;
  font-size: 16px;
  line-height: 1.5em;
  margin: 0 0 ${(props: { marginBottom: number}): number => props.marginBottom || 15}px;
  ${(props: any): string => config(props).media.lg`
    font-size: 18px;
  `}

  ol, li, ul, blockquote,img {
    display: none;
  }
  b {
    font-family: 'Good Sans Bold';
  }
`
const MobileSlider = styled.div`
  display: block;
  width: 100%;
  overflow: hidden;
  .swiper-pagination-border {
    border: 1px solid
      ${(props: any): any =>
        props.isDarkTheme ? colors.sand : colors.midnightBlue};
  }
  .swiper-pagination {
    padding: 0 !important;
    bottom: 0 !important;
  }
  ${(props: any): string => config(props).media.lg`
    display: none;
  `}
`
const DesktopSlider = styled.div`
  display: none;
  ${(props: any): string => config(props).media.lg`
    margin: 0 30px;
    display: flex;
    overflow: hidden;
    position: relative;
    .swiper-pagination-border {
      border: 1px solid ${(props: any): any =>
        props.isDarkTheme ? colors.sand : colors.midnightBlue};
      background: ${(props: any): any =>
        props.isDarkTheme ? colors.sand : colors.midnightBlue};
    }
    .swiper-pagination {
      box-sizing: border-box;
      bottom: 0 !important;
      text-align: right;
      padding-right: 20px;
    }
  `}
`
const SpotlightHr = styled.hr`
  height: 1px;
  background-color: ${(props: any): any =>
    props.isDarkTheme ? colors.sand : colors.midnightBlue};
  border: none;

  margin: 16px 0 8px 0;
  ${(props: any): string => config(props).media.sm`
     margin: 16px 12px 8px 10px;
  `}
  ${(props: any): string => config(props).media.lg`
     margin: 16px 0 8px 0;
  `}
`
const SectionHeader = styled(P)`
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.03em;
  font-weight: bold;
  color: ${(props: any): any =>
    props.isDarkTheme ? colors.sand : colors.midnightBlue};
  text-transform: uppercase;
  margin: 25px 0 32px 0;
  ${(props: any): string => config(props).media.sm`
    margin: 25px 0 34px 16px;
  `}
  ${(props: any): string => config(props).media.lg`
    margin: 25px 0 0 0;
  `}
`
const MobileSlide = styled.div`
  margin: 0 0 ${(props: any): string => (props.extraMargin ? '50px' : '30px')};
  ${(props: any): string => config(props).media.sm`
    margin: 0 15px ${(props: any): string =>
      props.extraMargin ? '50px' : '30px'};
  `}
`
const SpotlightImg = styled.div`
  margin: 0 0 25px;
  height: 100%;
  width: 100%;
  .fade-in-image {
    border-radius: 42px;
  }

  ${(props: any): string => config(props).media.sm`
    margin: 0 0 30px;
  `}
  ${(props: any): string => config(props).media.lg`
    margin: 0;
  `}
`
const FirstImageContainer = styled.div`
  height: 100%;
  width: calc(50% + 20px);
  margin: 0 20px 0 0;
  display: flex;
  align-items: center;
`

const FirstImage = styled.div`
  margin: 0 40px 0 0;
  .fade-in-image {
    border-radius: 42px;
  }
`
const MobileFirstImage = styled.div`
  margin: 0 15px 25px;
  .fade-in-image {
    border-radius: 42px;
  }
`
const DesktopView = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  > * {
    box-sizing: border-box;
    width: calc(50% - 20px);
  }
`
const NoImageDesktopVie = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  height: 100%;
  margin: 0 0 30px;
  flex-direction: row;
  justify-content: space-between;
`
const DesktopFirstImageOnly = styled.div`
  width: calc(50% - 20px);

  display: flex;
  justify-content: flex-end;
`
const DesktopNavigation = styled.div`
  box-sizing: border-box;
  position: absolute;
  right: 0;
  top: 0;
  width: calc(50% - 20px);
  z-index: 2;
  > div {
    display: flex;
    flex-direciton: row;
    justify-content: space-between;
    align-items: flex-start;
  }
`
const AlignBottomDiv = styled.div`
  margin: 100px 0
    ${(props: any): string => (props.extraMargin ? '50px' : '33px')};
  align-self: flex-end;
`
const ArrowWrapper = styled.div`
  display: flex;
  flex-direciton: row;
  align-items: center;
  justify-content: flex-start;
  margin: 20px 2px 0 0;
`
const ArrowButton = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 52px;
  width: 67px;
  background: none;
  border: none;
  opacity: 1;
  outline: none;
  border-radius: 10px;
  ${OutlineOnFocusVisible};
  svg {
    width: 100%;
    height: 100%;
  }
  &.previous {
    margin-right: 36px;
  }
  :hover,
  &.swiper-button-disabled {
    opacity: 0.5;
  }
`
